import type React from 'react';
import { useCallback, useMemo } from 'react';

import { EntryStatusEnum, EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import Grid from '@mui/material/Grid';
import { type Column, type Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { drawDefinitionConstants } from 'tods-competition-factory';

import { qualifyingEnabledAtomFamily } from 'atoms/events';
import CompetitorsTableEmptyState from 'components/tables/competitors/CompetitorsTableEmptyState';
import CompetitorsTableTitle from 'components/tables/competitors/CompetitorsTableTitle';
import {
  withdrawnPlayersTeamTableRequiredColumns,
  withdrawnTableRequiredColumns,
  withdrawnTeamTableRequiredColumns,
} from 'components/tables/competitors/config';
import { useCompetitorsTableColumns } from 'components/tables/competitors/useCompetitorsTableColumns';
import { getIsUpdateWithdrawnColumns } from 'components/tables/competitors/utils';
import DrawParticipantsTable from 'components/tables/eventsSplitDraws/drawParticipantsTable/DrawParticipantsTable';
import { BodyLargeLight } from 'components/typography/Typography';
import { usePlayersProfileDialog } from 'containers/dialogs/PlayersProfile';
import { usePromoteToMainDrawDialog } from 'containers/dialogs/PromoteToMainDraw';
import { useCompetitorsPopover } from 'containers/eventsSplitDraws/entries/EntriesActions';
import useMutateTournament from 'hooks/tournament/useMutateTournament';
import { useProgress } from 'hooks/tournament/useSaveTournament';
import useUISettings from 'hooks/useUISettings';
import type { ParticipantInterface } from 'typedefs/store/tmxTypes';
import { isStandalone } from 'utils/environment';

import { useStyles } from './styles';
import WithdrawnBulkActions from './WithdrawnBulkActions';

const { MAIN } = drawDefinitionConstants;

interface WithdrawnParticipantsTableProps {
  title?: string;
  event: any;
  isTeamTable?: boolean;
  tournamentId?: string;
  withdrawnParticipants: ParticipantInterface[];
}

const WithdrawnParticipantsTable: React.FC<React.PropsWithChildren<WithdrawnParticipantsTableProps>> = ({
  title,
  event,
  isTeamTable,
  tournamentId,
  withdrawnParticipants,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const openPopover = useCompetitorsPopover();
  const showProfileDialog = usePlayersProfileDialog();
  const openPromoteToMainDrawDialog = usePromoteToMainDrawDialog();
  const isQualifyingEnabled = useAtomValue(qualifyingEnabledAtomFamily(event?.eventId));

  const [moveTo, isLoadingMoveTo] = useMutateTournament('moveTo');
  const { tournamentDeskUI, updateSettings } = useUISettings() ?? {};

  const isStandAloneDemo = isStandalone();
  const mainDrawInProgress = useProgress('moveToMainDraw');
  const isDouble = event?.eventType === EventTypeEnum.DOUBLES;
  const isTeam = event?.eventType === EventTypeEnum.TEAM;
  const isTeamPlayersTable = isTeam && !isTeamTable;

  const handlePromoteWithdrawals = useCallback(
    (participantIds: string[], entryStage: 'MAIN' | 'QUALIFYING', resetRowSelection?: () => void) => {
      const selectedParticipants = withdrawnParticipants.filter((p) => participantIds.includes(p.participantId));
      const promotePlayers = async () => await moveTo(participantIds, event, isTeamPlayersTable, entryStage);
      isStandAloneDemo
        ? promotePlayers()
        : openPromoteToMainDrawDialog({
            entryStage: entryStage,
            eventsIds: [event?.eventId],
            eventType: event?.eventType,
            loading: mainDrawInProgress || isLoadingMoveTo,
            promoteParticipants: selectedParticipants,
            tournamentId: tournamentId,
            onPromotePlayers: promotePlayers,
            onPromoteTeamPlayers: isTeamPlayersTable
              ? () => moveTo(participantIds, event, isTeamPlayersTable, MAIN)
              : undefined,
            onResetRowSelection: resetRowSelection,
          });
    },
    [
      event,
      isLoadingMoveTo,
      isStandAloneDemo,
      isTeamPlayersTable,
      mainDrawInProgress,
      moveTo,
      openPromoteToMainDrawDialog,
      tournamentId,
      withdrawnParticipants,
    ],
  );

  const handleOnClickAction = useCallback(
    (participant: ParticipantInterface, clickEvent: React.MouseEvent<HTMLSpanElement, MouseEvent>) =>
      openPopover(
        participant,
        { onPromote: handlePromoteWithdrawals },
        clickEvent.currentTarget,
        event,
        isQualifyingEnabled,
      ),
    [event, handlePromoteWithdrawals, isQualifyingEnabled, openPopover],
  );

  const savedColumns = useMemo(() => {
    if (isTeamPlayersTable) return tournamentDeskUI?.teamsWithdrawnPlayersColumns;
    else if (isTeam && !isTeamPlayersTable) return tournamentDeskUI?.teamsWithdrawnTeamsColumns;
    else return tournamentDeskUI?.singlesDoublesWithdrawnColumns;
  }, [isTeam, isTeamPlayersTable, tournamentDeskUI]);

  const withdrawnTableColumns = useCompetitorsTableColumns({
    classes,
    event,
    isTeamTable,
    onClickAction: handleOnClickAction,
    onOpenPlayerProfile: showProfileDialog,
    requiredColumns: isTeamTable
      ? withdrawnTeamTableRequiredColumns
      : isTeamPlayersTable
        ? withdrawnPlayersTeamTableRequiredColumns
        : withdrawnTableRequiredColumns,
  });

  const renderWithdrawnBulkActions = useCallback(
    (selectedFlatRows: Row<ParticipantInterface>[], resetRowSelection?: () => void) => (
      <WithdrawnBulkActions
        isQualifyingEnabled={isQualifyingEnabled}
        selectedFlatRows={selectedFlatRows}
        onPromote={handlePromoteWithdrawals}
        onResetRowSelection={resetRowSelection}
      />
    ),
    [handlePromoteWithdrawals, isQualifyingEnabled],
  );

  const saveColumnsSelection = useCallback(
    (columns: Column<ParticipantInterface>[]) => {
      const selectedColumns = columns.map((column) => column.id);

      if (!getIsUpdateWithdrawnColumns({ isTeam, isTeamPlayersTable, tournamentDeskUI, selectedColumns })) return;

      const update = {
        teamsWithdrawnPlayersColumns: isTeamPlayersTable
          ? selectedColumns
          : tournamentDeskUI?.teamsWithdrawnPlayersColumns,
        teamsWithdrawnTeamsColumns:
          isTeam && !isTeamPlayersTable ? selectedColumns : tournamentDeskUI?.teamsWithdrawnTeamsColumns,
        singlesDoublesWithdrawnColumns: !isTeam ? selectedColumns : tournamentDeskUI?.singlesDoublesWithdrawnColumns,
      };
      updateSettings(update);
    },
    [updateSettings, tournamentDeskUI, isTeam, isTeamPlayersTable],
  );

  return (
    <>
      {!!title && (
        <Grid container direction="row" justifyContent="space-between">
          <Grid item>
            <CompetitorsTableTitle length={`${withdrawnParticipants.length}`} text={title || t('withdrawals')} />
          </Grid>
        </Grid>
      )}
      {withdrawnParticipants?.length > 0 ? (
        <DrawParticipantsTable
          isEnableMultiRowSelection
          columns={withdrawnTableColumns}
          data={withdrawnParticipants}
          isDouble={isDouble}
          saveColumnsSelection={saveColumnsSelection}
          savedColumns={savedColumns}
          selectedActions={renderWithdrawnBulkActions}
          type={EntryStatusEnum.WITHDRAWN}
        />
      ) : (
        <CompetitorsTableEmptyState>
          <BodyLargeLight>{isTeamTable ? t('noWithdrawnTeams') : t('noWithdrawnPlayers')}</BodyLargeLight>
        </CompetitorsTableEmptyState>
      )}
    </>
  );
};

export default WithdrawnParticipantsTable;
